/**
 * A transient, floating one-line text field for the Text tool (ADR 0033).
 *
 * Shared by the plan canvas and the elevation: when the designer taps in text
 * mode, this drops an <input> at the tap (fixed to the viewport, so it needs no
 * positioned ancestor) and hands back the typed value on Enter, or when the
 * designer taps OUTSIDE the box. Escape cancels. It removes itself either way.
 *
 * It deliberately does NOT commit on `blur`: the very tap that opens the box
 * fires a pointerup/click that would blur and remove it before anyone can type
 * (owner bug). Instead focus + the outside-tap listener are attached one tick
 * later, after that opening tap has fully settled.
 *
 * @param {number} clientX viewport x of the tap
 * @param {number} clientY viewport y of the tap
 * @param {string} [color] text colour (the current pen colour)
 * @param {(value:string)=>void} onCommit called with the final string (may be
 *        empty; the caller decides whether to keep it)
 */
export function floatTextInput(clientX, clientY, color, onCommit) {
  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'text-note-input';
  input.setAttribute('aria-label', 'Note text');
  input.style.left = `${clientX}px`;
  input.style.top = `${clientY}px`;
  if (color) input.style.color = color;
  document.body.appendChild(input);

  let done = false;
  const onOutside = (e) => { if (e.target !== input) finish(true); };
  const finish = (save) => {
    if (done) return;
    done = true;
    document.removeEventListener('pointerdown', onOutside, true);
    const value = input.value;
    input.remove();
    if (save) onCommit(value);
  };

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); finish(true); }
    else if (e.key === 'Escape') { e.preventDefault(); finish(false); }
  });

  // Focus + arm the outside-tap dismiss only after the opening tap settles, so
  // that tap's own pointerup/click can't steal focus or count as "outside".
  setTimeout(() => {
    if (done) return;
    input.focus();
    document.addEventListener('pointerdown', onOutside, true);
  }, 60);
}
